import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import CloudDoneIcon from '@mui/icons-material/CloudDone';

const connectedSources = [
  { name: 'QuickBooks Online', status: 'connected', lastSync: '2 minutes ago', records: '12,450' },
  { name: 'Shopify', status: 'connected', lastSync: '5 minutes ago', records: '8,920' },
  { name: 'Stripe', status: 'syncing', lastSync: 'In progress...', records: '3,200' },
  { name: 'Google Analytics', status: 'connected', lastSync: '1 hour ago', records: '45,000' },
  { name: 'HubSpot CRM', status: 'connected', lastSync: '30 minutes ago', records: '2,100' },
];

const availableSources = [
  'Xero', 'FreshBooks', 'Square', 'PayPal', 'MailChimp', 'Salesforce', 'WooCommerce', 'BigCommerce'
];

const SampleData: React.FC = () => {
  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
        Data Sources
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Manage your connected data sources and integrations
      </Typography>

      {/* Connected Sources */}
      <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600, mb: 2 }}>
        Connected Sources
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {connectedSources.map((source) => (
          <Grid item xs={12} sm={6} md={4} key={source.name}>
            <Card sx={{ border: 1, borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                    {source.name}
                  </Typography>
                  <Chip
                    icon={source.status === 'syncing' ? <SyncIcon sx={{ fontSize: 16 }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label={source.status === 'syncing' ? 'Syncing' : 'Connected'}
                    size="small"
                    sx={{
                      bgcolor: source.status === 'syncing' ? 'info.light' : 'success.light',
                      color: source.status === 'syncing' ? 'info.dark' : 'success.dark',
                    }}
                  />
                </Box>
                {source.status === 'syncing' && (
                  <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Last sync
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                      {source.lastSync}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Records
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                      {source.records}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Available Integrations */}
      <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600, mb: 2 }}>
        Available Integrations
      </Typography>
      <Card sx={{ border: 1, borderColor: 'divider', p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {availableSources.map((source) => (
            <Chip
              key={source}
              icon={<CloudDoneIcon />}
              label={source}
              variant="outlined"
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                cursor: 'pointer',
              }}
            />
          ))}
          <Chip
            label="+100 more"
            sx={{
              bgcolor: 'background.lightBlue',
              color: 'secondary.main',
              fontWeight: 600,
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default SampleData;
